import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  CreditNote,
  CreditNoteLine,
  CreditNoteListResponse,
  CreditNoteListItem,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Credit Notes management service (real ERP APIs)
 */
export class CreditNotesService {
  /**
   * Get paginated list of credit notes for a branch
   */
  async getAllCreditNotes(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<CreditNoteListResponse> {
    const url = appendSortDir(endpoints.creditNotes.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<CreditNoteListResponse> | CreditNoteListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as CreditNoteListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as CreditNoteListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as CreditNoteListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Get credit note by ID (for edit)
   */
  async getCreditNoteById(branchId: number, creditNoteId: number): Promise<CreditNote> {
    const url = endpoints.creditNotes.getById(branchId, creditNoteId)
    console.log('=== CreditNotesService.getCreditNoteById ===')
    console.log('URL:', url)
    console.log('==========================================')
    
    const response = await apiClient.get<ApiResponse<CreditNote> | CreditNote | any>(url)
    
    console.log('=== API Response ===')
    console.log('Response data:', JSON.stringify(response.data, null, 2))
    console.log('Response data type:', Array.isArray(response.data) ? 'Array' : typeof response.data)
    console.log('====================')
    
    let creditNoteData: any
    
    // Check if response is directly an array (lines only)
    if (Array.isArray(response.data)) {
      console.log('Response is array of lines, count:', response.data.length)
      // If response is array, it's just the lines
      creditNoteData = {
        creditNoteId: creditNoteId,
        creditNoteLines: response.data,
      }
    } else if ('data' in response.data && 'success' in response.data) {
      creditNoteData = response.data.data
    } else if ('data' in response.data) {
      creditNoteData = response.data.data
    } else {
      creditNoteData = response.data
    }
    
    return this.mapCreditNoteData(creditNoteData, branchId)
  }

  /**
   * Map API response to CreditNote interface
   */
  private mapCreditNoteData(creditNoteData: any, branchId: number): CreditNote {
    // Map credit note lines if available
    const creditNoteLines: CreditNoteLine[] = []
    
    console.log('Mapping credit note data:', {
      hasCreditNoteLines: !!creditNoteData.creditNoteLines,
      hasCrInvoiceLine: !!creditNoteData.crInvoiceLine,
      isArray: Array.isArray(creditNoteData.creditNoteLines),
    })
    
    if (creditNoteData.creditNoteLines && Array.isArray(creditNoteData.creditNoteLines)) {
      creditNoteLines.push(...creditNoteData.creditNoteLines.map((line: any) => {
        // Calculate taxableAmount if missing
        const itemPrice = line.itemPrice || (line.qty || 0) * (line.unitPrice || 0)
        const discountAmount = line.discountAmount || itemPrice * ((line.discountPercent || 0) / 100)
        const taxableAmount = line.taxableAmount || (itemPrice - discountAmount)
        
        return {
          lineId: line.lineId || 0,
          invLineId: line.invLineId || 0,
          creditNoteId: line.creditNoteId || creditNoteData.creditNoteId || 0,
          itemId: line.itemId || 0,
          description: line.description || '',
          qty: line.qty || 0,
          unitPrice: line.unitPrice || 0,
          itemPrice: itemPrice,
          discountPercent: line.discountPercent || 0,
          discountAmount: discountAmount,
          taxableAmount: taxableAmount,
          taxRateId: line.taxRateId || 0,
          taxRate: line.taxRate || 0,
          taxPercent: line.taxPercent || line.taxRate || 0,
          taxAmount: line.taxAmount || 0,
          lineAmount: line.lineAmount || 0,
          companyId: line.companyId || creditNoteData.companyId,
          branchId: line.branchId || branchId,
        }
      }))
    } else if (creditNoteData.crInvoiceLine && Array.isArray(creditNoteData.crInvoiceLine)) {
      // Alternative field name
      creditNoteLines.push(...creditNoteData.crInvoiceLine.map((line: any) => {
        // Calculate taxableAmount if missing
        const itemPrice = line.itemPrice || (line.qty || 0) * (line.unitPrice || 0)
        const discountAmount = line.discountAmount || itemPrice * ((line.discountPercent || 0) / 100)
        const taxableAmount = line.taxableAmount || (itemPrice - discountAmount)
        
        return {
          lineId: line.lineId || 0,
          invLineId: line.invLineId || 0,
          creditNoteId: line.creditNoteId || creditNoteData.creditNoteId || 0,
          itemId: line.itemId || 0,
          description: line.description || '',
          qty: line.qty || 0,
          unitPrice: line.unitPrice || 0,
          itemPrice: itemPrice,
          discountPercent: line.discountPercent || 0,
          discountAmount: discountAmount,
          taxableAmount: taxableAmount,
          taxRateId: line.taxRateId || 0,
          taxRate: line.taxRate || 0,
          taxPercent: line.taxPercent || line.taxRate || 0,
          taxAmount: line.taxAmount || 0,
          lineAmount: line.lineAmount || 0,
          companyId: line.companyId || creditNoteData.companyId,
          branchId: line.branchId || branchId,
        }
      }))
    }
    
    console.log('Mapped credit note lines count:', creditNoteLines.length)
    
    return {
      creditNoteId: creditNoteData.creditNoteId,
      seq: creditNoteData.seq,
      creditNoteDate: creditNoteData.creditNoteDate || '',
      creditNoteNumber: creditNoteData.creditNoteNo || creditNoteData.creditNoteNumber || '',
      creditNoteNo: creditNoteData.creditNoteNo,
      customerId: creditNoteData.customerId || 0,
      customerName: creditNoteData.customerName,
      supplyDate: creditNoteData.supplyDate || '',
      refInvNumber: creditNoteData.refInvNumber,
      refInvId: creditNoteData.refInvId || creditNoteData.invoiceId || creditNoteData.InvoiceId,
      currency: creditNoteData.currency || 0,
      conversionRate: creditNoteData.conversionRate || 1,
      taxRateId: creditNoteData.taxRateId || null,
      uom: creditNoteData.uom,
      subTotal: creditNoteData.subTotal || 0,
      totalTax: creditNoteData.totalTax || 0,
      discountPer: creditNoteData.discountPer,
      totalDiscount: creditNoteData.totalDiscount || 0,
      total: creditNoteData.total || 0,
      note: creditNoteData.note || '',
      isPosted: creditNoteData.isPosted,
      postedDate: creditNoteData.postedDate,
      postedBy: creditNoteData.postedBy,
      glBatchId: creditNoteData.glBatchId,
      companyId: creditNoteData.companyId,
      branchId: creditNoteData.branchId || branchId,
      portalId: creditNoteData.portalId,
      createdDate: creditNoteData.createdDate,
      createdBy: creditNoteData.createdBy,
      modifiedDate: creditNoteData.modifiedDate,
      modifiedBy: creditNoteData.modifiedBy,
      isFullyRefund: creditNoteData.isFullyRefund,
      creditNoteType: Number(creditNoteData.creditNoteType ?? creditNoteData.CreditNoteType ?? 1),
      remainingAmount: creditNoteData.remainingAmount,
      creditNoteLines: creditNoteLines,
    } as CreditNote
  }

  /**
   * Get all invoices (for reference invoice selection)
   * @param branchId - Branch ID
   * @param customerId - Optional customer ID to filter invoices
   */
  async getAllInvoices(
    branchId: number,
    customerId?: number,
    creditNoteType?: number,
    excludeCreditNoteId?: number
  ): Promise<any[]> {
    const url = endpoints.creditNotes.getAllInvoices(branchId, customerId, creditNoteType, excludeCreditNoteId)
    console.log('Fetching invoices from:', url)
    const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
    console.log('Invoices API response:', response.data)
    
    if (Array.isArray(response.data)) {
      console.log('Returning array directly, count:', response.data.length)
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      console.log('Returning data.data array, count:', response.data.data.length)
      return response.data.data
    }
    if ('success' in response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      console.log('Returning success.data array, count:', response.data.data.length)
      return response.data.data
    }
    console.warn('No invoice data found in response, returning empty array')
    return []
  }

  /**
   * Get all invoice lines (for reference invoice line selection)
   */
  async getAllInvoiceLines(branchId: number): Promise<any[]> {
    const url = endpoints.creditNotes.getAllInvoiceLines(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  private asLineArray(raw: any): any[] {
    if (raw == null || raw === '') return []
    if (typeof raw === 'string') {
      try {
        return this.asLineArray(JSON.parse(raw))
      } catch {
        return []
      }
    }
    if (Array.isArray(raw)) return raw.filter((x) => x != null)
    if (Array.isArray(raw.$values)) return raw.$values.filter((x: any) => x != null)
    if (typeof raw !== 'object') return []

    const keys = Object.keys(raw)
    const numericKeys = keys.filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b))
    if (numericKeys.length > 1) {
      return numericKeys.map((k) => raw[k]).filter((x) => x != null)
    }

    if (raw.lineId != null || raw.LineId != null || raw.description != null || raw.Description != null) {
      return [raw]
    }
    return []
  }

  private unwrapInvLinesPayload(payload: any, depth = 0): any {
    if (payload == null || depth > 6) return payload
    if (Array.isArray(payload)) {
      return { invoiceLines: payload }
    }
    if (typeof payload !== 'object') return payload

    const rawLines = payload.invoiceLines ?? payload.InvoiceLines ?? payload.lines ?? payload.Lines
    if (rawLines != null) {
      return {
        ...payload,
        invoiceLines: this.asLineArray(rawLines),
        invoice: payload.invoice ?? payload.Invoice ?? {},
        maxCreditAmount: payload.maxCreditAmount ?? payload.MaxCreditAmount ?? 0,
      }
    }

    const nested = payload.data ?? payload.result ?? payload.Result
    if (nested != null) {
      const unwrapped = this.unwrapInvLinesPayload(nested, depth + 1)
      if (unwrapped) return unwrapped
    }

    return payload
  }

  private normalizeInvoiceSourceLine(line: any): any {
    if (!line || typeof line !== 'object') return line
    const originalQty = Number(line.originalQty ?? line.OriginalQty ?? line.qty ?? line.Qty ?? 0)
    const qtyEditable = line.qtyEditable ?? line.QtyEditable ?? originalQty > 1
    return {
      ...line,
      lineId: Number(line.lineId ?? line.LineId ?? 0),
      invLineId: Number(line.invLineId ?? line.InvLineId ?? line.lineId ?? line.LineId ?? 0),
      invoiceId: Number(line.invoiceId ?? line.InvoiceId ?? 0),
      itemId: Number(line.itemId ?? line.ItemId ?? 0),
      description: line.description ?? line.Description ?? '',
      qty: Number(line.qty ?? line.Qty ?? 0),
      unitPrice: Number(line.unitPrice ?? line.UnitPrice ?? 0),
      itemPrice: Number(line.itemPrice ?? line.ItemPrice ?? 0),
      discountPercent: Number(line.discountPercent ?? line.DiscountPercent ?? 0),
      discountAmount: Number(line.discountAmount ?? line.DiscountAmount ?? 0),
      taxableAmount: Number(line.taxableAmount ?? line.TaxableAmount ?? 0),
      taxRateId: Number(line.taxRateId ?? line.TaxRateId ?? 0),
      taxRate: Number(line.taxRate ?? line.TaxRate ?? line.taxPercent ?? 0),
      taxPercent: Number(line.taxPercent ?? line.taxRate ?? line.TaxRate ?? 0),
      taxAmount: Number(line.taxAmount ?? line.TaxAmount ?? 0),
      lineAmount: Number(line.lineAmount ?? line.LineAmount ?? 0),
      originalQty,
      qtyEditable,
      unitPriceEditable: line.unitPriceEditable ?? line.UnitPriceEditable ?? !qtyEditable,
      maxQty: Number(line.maxQty ?? line.MaxQty ?? line.qty ?? line.Qty ?? 0),
      maxUnitPrice: Number(line.maxUnitPrice ?? line.MaxUnitPrice ?? line.unitPrice ?? line.UnitPrice ?? 0),
      maxLineAmount: Number(line.maxLineAmount ?? line.MaxLineAmount ?? line.lineAmount ?? line.LineAmount ?? 0),
    }
  }

  /**
   * Get invoice lines by invoice ID
   * Returns structure: { invoice: {...}, invoiceLines: [...] }
   */
  async getInvLines(
    invoiceId: number,
    branchId: number,
    excludeCreditNoteId?: number
  ): Promise<{ invoice: any; invoiceLines: any[]; maxCreditAmount?: number }> {
    const url = endpoints.creditNotes.getInvLines(invoiceId, branchId, excludeCreditNoteId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const data = this.unwrapInvLinesPayload(response.data)
    const raw = data?.invoiceLines ?? data?.InvoiceLines
    const invoiceLines = this.asLineArray(raw).map((line) => this.normalizeInvoiceSourceLine(line))
    return {
      invoice: data?.invoice ?? data?.Invoice ?? {},
      invoiceLines,
      maxCreditAmount: Number(data?.maxCreditAmount ?? data?.MaxCreditAmount ?? 0),
    }
  }

  private assertApiSuccess(data: any, fallback: string) {
    const result = data?.result ?? data
    if (result && (result.isError === true || result.IsError === true)) {
      const err: any = new Error(result.msg || result.message || fallback)
      err.response = { status: 200, data }
      throw err
    }
    return data
  }

  /**
   * Insert credit note
   */
  async insertCreditNote(creditNote: any): Promise<CreditNote> {
    console.log('=== CreditNotesService.insertCreditNote ===')
    console.log('Endpoint:', endpoints.creditNotes.insert)
    console.log('Request Payload:', JSON.stringify(creditNote, null, 2))
    console.log('===========================================')
    
    const response = await apiClient.post<ApiResponse<CreditNote> | CreditNote>(endpoints.creditNotes.insert, creditNote)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')

    this.assertApiSuccess(response.data, 'Failed to save credit note.')
    
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<CreditNote>).data
    }
    return response.data as CreditNote
  }

  /**
   * Update credit note
   */
  async updateCreditNote(creditNote: any): Promise<CreditNote> {
    console.log('=== CreditNotesService.updateCreditNote ===')
    console.log('Endpoint:', endpoints.creditNotes.update)
    console.log('Request Payload:', JSON.stringify(creditNote, null, 2))
    console.log('==========================================')
    
    const response = await apiClient.put<ApiResponse<CreditNote> | CreditNote>(endpoints.creditNotes.update, creditNote)
    
    console.log('=== API Response ===')
    console.log('Response Status:', response.status)
    console.log('Response Data:', JSON.stringify(response.data, null, 2))
    console.log('====================')

    this.assertApiSuccess(response.data, 'Failed to update credit note.')
    
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<CreditNote>).data
    }
    return response.data as CreditNote
  }

  /**
   * Get user IP address
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Post credit note (mark as posted)
   */
  async postCreditNote(id: number, ipAddress?: string): Promise<ApiResponse | { success?: boolean; message?: string }> {
    // Get IP address if not provided (this gets the logged-in user's IP, not server IP)
    const userIP = ipAddress || await this.getUserIPAddress()
    const url = endpoints.creditNotes.postData(id, userIP)
    
    // Prepare payload for logging
    const payload = {
      creditNoteId: id,
      ipAddress: userIP,
      endpoint: url,
    }
    
    console.log('%c=== CreditNotesService.postCreditNote ===', 'background: #9C27B0; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cCredit Note ID:', 'color: #FF9800; font-weight: bold;', id)
    console.log('%cIP Address (User):', 'color: #4CAF50; font-weight: bold;', userIP)
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%cPayload:', 'color: #9C27B0; font-weight: bold;', JSON.stringify(payload, null, 2))
    console.log('%cPayload Object:', 'color: #009688; font-weight: bold;', payload)
    console.log('%c=========================================', 'background: #9C27B0; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.post<ApiResponse | { success?: boolean; message?: string }>(url)
    
    console.log('%c=== API Response ===', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;')
    console.log('%cResponse Status:', 'color: #2196F3; font-weight: bold;', response.status)
    console.log('%cResponse Data:', 'color: #4CAF50; font-weight: bold;', JSON.stringify(response.data, null, 2))
    console.log('%c====================', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;')

    this.assertApiSuccess(response.data, 'Failed to post credit note.')
    
    return response.data
  }
}

export const creditNotesService = new CreditNotesService()

